import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { type GridColDef, type GridSortModel } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { extractApiErrorMessage } from 'utils/helpers/apiError';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import MaterialCategoryAPI from 'api/material-category.api';
import { type MaterialCategoryRecord } from './types';
import { AddMaterialCategoryModal } from './AddMaterialCategoryModal';
import { EditMaterialCategoryModal } from './EditMaterialCategoryModal';

interface Props {
  records: MaterialCategoryRecord[];
  loading: boolean;
  onRefetch: () => Promise<void>;
}

const PAGE_SIZE = 25;

export const MaterialCategoryTab = ({
  records,
  loading,
  onRefetch,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const materialCategoryApi = useRef(new MaterialCategoryAPI()).current;

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<MaterialCategoryRecord | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] =
    useState<MaterialCategoryRecord | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return records;
    return records.filter(
      (r) =>
        r.enumValue.toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q)
    );
  }, [records, searchQuery]);

  const sorted = useMemo(() => {
    if (sortModel.length === 0) return filtered;
    const { field, sort } = sortModel[0];
    const dir = sort === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const av = String(a[field as keyof MaterialCategoryRecord] ?? '');
      const bv = String(b[field as keyof MaterialCategoryRecord] ?? '');
      return av.localeCompare(bv) * dir;
    });
  }, [filtered, sortModel]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const paged = sorted.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await materialCategoryApi.remove(deleteTarget.enumValue);
      await onRefetch();
      dispatch(
        showNotification({
          message: t('material_category_toast_deleted', {
            code: deleteTarget.enumValue,
          }),
          type: 'success',
        })
      );
    } catch (e) {
      const message = extractApiErrorMessage(
        e,
        t('material_category_toast_delete_failed')
      );
      dispatch(showNotification({ message, type: 'error' }));
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
      setDeleteConfirmText('');
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'enumValue',
        headerName: t('material_category_col_code'),
        minWidth: 160,
        flex: 1,
      },
      {
        field: 'category',
        headerName: t('material_category_col_category'),
        minWidth: 200,
        flex: 1,
      },
      {
        field: 'actions',
        headerName: t('material_category_col_actions'),
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={t('material_category_edit')}>
              <IconButton
                size="small"
                onClick={() => {
                  setEditRecord(params.row as MaterialCategoryRecord);
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('material_category_delete')}>
              <IconButton
                size="small"
                onClick={() => {
                  setDeleteTarget(params.row as MaterialCategoryRecord);
                  setDeleteConfirmText('');
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [t]
  );

  return (
    <Box
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {t('material_category_all')}{' '}
          <Typography
            component="span"
            variant="body2"
            color="primary.main"
            fontWeight={700}
          >
            ({records.length})
          </Typography>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder={t('material_category_search_placeholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery('');
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ width: 220 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setAddOpen(true);
            }}
            sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
          >
            {t('material_category_btn_add')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (th: Theme) => alpha(th.palette.background.paper, 0.6),
              zIndex: 10,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        <CustomStyledDataGrid
          rows={paged}
          columns={columns}
          getRowId={(row) => row.enumValue}
          disableRowSelectionOnClick
          hideFooter
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: (th: Theme) => alpha(th.palette.divider, 0.4),
        }}
      >
        <IconButton
          size="small"
          onClick={() => {
            setPage((p) => Math.max(0, p - 1));
          }}
          disabled={safePage === 0}
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: 80, textAlign: 'center' }}
        >
          {safePage + 1} / {pageCount}
        </Typography>
        <IconButton
          size="small"
          onClick={() => {
            setPage((p) => Math.min(pageCount - 1, p + 1));
          }}
          disabled={safePage >= pageCount - 1}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <AddMaterialCategoryModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onCreated={() => {
          setAddOpen(false);
          void onRefetch();
        }}
      />
      <EditMaterialCategoryModal
        open={editRecord !== null}
        record={editRecord}
        onClose={() => {
          setEditRecord(null);
        }}
        onSaved={() => {
          setEditRecord(null);
          void onRefetch();
        }}
      />

      <Dialog
        open={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteConfirmText('');
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th: Theme) => alpha(th.palette.error.main, 0.25),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
          {t('material_category_delete_confirm_title')}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1.5, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('material_category_delete_confirm_instruction')}
          </Typography>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder={deleteTarget?.enumValue ?? ''}
            value={deleteConfirmText}
            onChange={(e) => {
              setDeleteConfirmText(e.target.value);
            }}
            error={
              deleteConfirmText.length > 0 &&
              deleteConfirmText !== deleteTarget?.enumValue
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setDeleteTarget(null);
              setDeleteConfirmText('');
            }}
          >
            {t('material_category_cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteConfirmText !== deleteTarget?.enumValue || deleting}
            onClick={() => {
              void handleDelete();
            }}
            startIcon={
              deleting ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            {t('material_category_delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
